// System Health Page
import React, { useState, useEffect } from 'react';
import { useAdminTranslation } from '../utils/i18nContext';
import { adminApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, CheckCircle, Database } from 'lucide-react';

const SystemHealth = () => {
  const { t } = useAdminTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHealth();
  }, []);

  const loadHealth = async () => {
    try {
      const result = await adminApi.getSystemHealth();
      setData(result);
    } catch (error) {
      console.error('Failed to load system health:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400 py-12">{t('loading')}</div>;
  }

  if (!data) {
    return <div className="text-center text-gray-400 py-12">{t('error')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">{t('system_health')}</h2>
        <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
          <CheckCircle className="w-4 h-4 mr-2" />
          {t('healthy')}
        </Badge>
      </div>

      {/* Database Stats */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Database className="w-5 h-5 text-cyan-400" />
            {t('database_stats')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
              <p className="text-xs text-cyan-300 mb-1">{t('users')}</p>
              <p className="text-2xl font-bold text-cyan-400">{data.stats.users.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
              <p className="text-xs text-purple-300 mb-1">{t('sessions')}</p>
              <p className="text-2xl font-bold text-purple-400">{data.stats.sessions.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
              <p className="text-xs text-green-300 mb-1">Events</p>
              <p className="text-2xl font-bold text-green-400">{data.stats.events.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Events */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Activity className="w-5 h-5 text-cyan-400" />
            {t('recent_events')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_events.length === 0 ? (
            <p className="text-gray-400 text-center py-4">{t('no_data')}</p>
          ) : (
            <div className="space-y-2">
              {data.recent_events.map((event, idx) => (
                <div key={idx} className="p-3 bg-white/5 rounded border border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{event.event_type}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {event.mode && (
                    <span className="text-xs text-gray-500">Mode: {event.mode}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Audit Logs */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white">{t('recent_audit_logs')}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_audit.length === 0 ? (
            <p className="text-gray-400 text-center py-4">{t('no_data')}</p>
          ) : (
            <div className="space-y-2">
              {data.recent_audit.map((log, idx) => (
                <div key={idx} className="p-3 bg-yellow-500/5 rounded border border-yellow-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{log.action}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">{log.actor_email}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealth;
